// Forked from https://github.com/shuding/stable-hash
// Created by Shu Ding. Released under the MIT License.

// Use WeakMap to store the object-key mapping so the objects can still be
// garbage collected. WeakMap uses a hashtable under the hood, so the lookup
// complexity is almost O(1).
const table = new WeakMap<object, string>();

// A counter of the key.
let counter = 0;

// A stable hash implementation that supports:
//  - Fast and ensures unique hash properties
//  - Handles unserializable values
//  - Handles object key ordering
//  - Generates short results
//
// This is not a serialization function, and the result is not guaranteed to be
// parsable.
export default function stableHash(arg: any): string {
  const type = typeof arg;
  const constructor = arg && arg.constructor;
  const isDate = constructor === Date;

  if (Object(arg) === arg && !isDate && constructor !== RegExp) {
    // Object/function, not null/date/regexp. Use WeakMap to store the id first.
    // If it's already hashed, directly return the result.
    let result = table.get(arg);
    if (result) {
      return result;
    }
    // Store the hash first for circular reference detection before entering the
    // recursive `stableHash` calls.
    // For other objects like set and map, we use this id directly as the hash.
    result = ++counter + "~";
    table.set(arg, result);
    let index: any;

    const name = constructor.name;
    if (name && name.substring(name.length - 5) === "Array") {
      // Array or TypedArray.
      result = "@";
      for (index = 0; index < arg.length; index++) {
        result += stableHash(arg[index]) + ",";
      }
      table.set(arg, result);
    } else if (name === "Object") {
      // Object, sort keys.
      const keys = Object.keys(arg).sort();
      result = "#";
      for (const index of keys) {
        if (arg[index] !== undefined) {
          result += index + ":" + stableHash(arg[index]) + ",";
        }
      }
      table.set(arg, result);
    }
    return result;
  }
  if (isDate) {
    return arg.toJSON();
  }
  if (type === "symbol") {
    return arg.toString();
  }
  return type === "string" ? JSON.stringify(arg) : "" + arg;
}
